
import { isSpotStaff } from '../utils/staffPriority';
import { useCustomerFlags } from '../hooks/useCustomerFlags';
import CustomerMarkIcon from './CustomerMarkIcon';
import React, { useState, useEffect, useMemo } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Box,
  Tooltip,
  Divider,
  ListSubheader,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Grid,
  Button,
  keyframes,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Collapse
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import { LINE_CHAT_COLOR, RESV_CHAT_COLOR, CHAT_ICON_DISABLED_COLOR } from '../constants/chatIcons';
import VehicleCheckBadges from './VehicleCheckBadges';
import { flightStatusColor } from '../services/nahaFlightService';
import dayjs from 'dayjs';
import ReservationEditorPanel from './editor2';
import { COMMUNICATION_MEDIA_COLORS, DEFAULT_VISIBLE_STATUS_CODES } from '../constants/status';


import { toEditorReservation } from '../lib/editorDataTransform';
import { reservationSyncService } from '../services/reservationSyncService';
import { toggleReservationLicense, toggleReservationAgreement } from '../api/dispatchService';
import { PICKUP_SUB_ZONES, RETURN_SUB_ZONES } from './VehiclePositionMap';
import { computePaymentStatus } from '../utils/paymentStatus';
import { UI } from '../styles/uiTokens';
import { truncStatusName, shortVehicleCode } from '../lib/displayFormat';
import SpacedLabel from './SpacedLabel';
import { isReservationInProgress } from '../lib/reservationProgress';
import { getArrivalContact, AIRPORT_CALLED, CALLED_COLOR } from '../lib/arrivalContact';
import { isUsMilitaryReservation, US_MILITARY_COLORS } from '../lib/reservationKind';
import { isLineEnabled } from '../config/backend';
import { useReservationChat } from '../hooks/useReservationChat';
import { reservationChatByReservation, isSessionUnreadForStaff } from '../lib/reservationChat';
import { findCarType } from '../utils/carTypeMatch';
import { rowPrefixOf, rowPrefixTitleOf, rowOutlineOf, hotelDeliveryMarkOf, cruiseMarkOf } from '../utils/planPrefix';

// デフォルトの予約ステータス（propsが渡されない場合のフォールバック）
const DEFAULT_RESERVATION_STATUS_LIST = [];

// 完了行（当日処理済み）の背景色。グレー(bgSubtle)では未完了と見分けづらいという
// 現場の声を受け、淡い緑ではっきり区別する（2026-07-26）。✓は緑丸バッジで表示
const DONE_ROW_BG = '#e0f2e5';

// 「対応中」の行（迎えに向かっている貸出 / 戻ってきて手続き中の返却）は黄色で点滅させる
// （2026-09-14 前田さん指示）。判定は lib/reservationProgress.ts
const IN_PROGRESS_PULSE = keyframes`
  0%, 100% { background-color: #fffde7; }
  50% { background-color: #fff176; }
`;

// ターンアラウンド（当日中に返却 → 同じ車両を再貸出）のインジケータ色。
// 通常は赤。返却時刻と貸出時刻がぴったり同じ場合は、間に余裕がまったく無く
// 段取りの難度が跳ね上がるため、赤と一目で区別できるショッキングピンクにする（2026-08-25）
export const TURNAROUND_COLOR = '#d32f2f';
export const TURNAROUND_SAME_TIME_COLOR = '#ff1493';

// リスト内ラベルの共通幅（2026-08-25）。
// 予約/車両ステータスのチップ・連絡/運転者/対応者ボタンをすべてこの幅に揃える。
// 中身は3文字ぶん + 左右 0.1em ずつの余白。
//
// **rem で固定すること**。以前 calc(3rem + 0.2em) のように em を混ぜていたため、
// フォントサイズが違うチップ(0.7rem)とボタン(0.65rem)で実寸が約5px ずれ、
// 運転者ボタンだけ大きく見えていた。
const LIST_LABEL_WIDTH = '2.85rem';

// ステータスチップ（車両/予約とも truncStatusName で3文字幅に揃う）の共通スタイル。
// 2026-08-10: 左右の余白が大きく間延びして見えたため 3.6rem → 2.7rem に圧縮した
const STATUS_CHIP_WIDTH = LIST_LABEL_WIDTH;
// 2026-08-25: ラベルは全角スペース詰め（店　舗）をやめ、CSS の letter-spacing で
// 2文字を3文字ぶんの幅に広げる（<SpacedLabel> が字間で広げる）。
// ラベルが3文字幅を占めるぶん左右が窮屈になるので、箱の幅に 0.1em×2 の余白を足す
const STATUS_CHIP_BASE: React.CSSProperties = {
  padding: '1px 2px', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 'bold',
  cursor: 'pointer', flexShrink: 0, display: 'inline-block', textAlign: 'center',
  whiteSpace: 'nowrap', boxSizing: 'border-box', width: STATUS_CHIP_WIDTH, overflow: 'hidden',
};

// 2段行レイアウトの行高（2026-08-10）。
// 列ごとに要素の高さが違うと「未設定」とフライト情報のように上下の中心線がバラバラになるため、
// 上段・下段それぞれを固定高さのボックスに入れ、その中で天地中央に置いて水平中心線を揃える。
const ROW_TOP_H = 22;   // 上段の行高(px)。最も背の高い要素=ボタン類に合わせる
const ROW_BOT_H = 16;   // 下段の行高(px)。掃洗Mバッジ(15px)が収まる高さ
const ROW_LINE_GAP = '4px'; // 上段と下段の余白（くっつき過ぎを解消）
const cellTopSx = { height: `${ROW_TOP_H}px`, display: 'flex', alignItems: 'center', flexShrink: 0 } as const;
const cellBotSx = { height: `${ROW_BOT_H}px`, display: 'flex', alignItems: 'center', flexShrink: 0 } as const;

// 連絡・運転者・対応者ボタン（すべて truncStatusName で3文字幅）の共通スタイル。
// 幅を揃えることで「直　来 / 送迎車」「運転者 / タイミ」が縦に整列する
const STAFF_BTN_SX = {
  // width はステータスチップと共通（LIST_LABEL_WIDTH）。列を揃えるため必ず同じ値を使う
  fontSize: '0.65rem', padding: '2px 2px', minWidth: 'auto', width: LIST_LABEL_WIDTH,
  boxShadow: 'none', flexShrink: 0, lineHeight: 1.5,
} as const;

const getColumns = (isMobile) => [
  { id: 'name', label: '名前', sticky: true, left: 0, minWidth: isMobile ? 120 : 150 },
  { id: 'vehicle', label: isMobile ? '予約一覧' : '車両', sticky: true, left: isMobile ? 120 : 150, minWidth: 100 },
  { id: 'time', label: '時間', sticky: !isMobile, left: 250, minWidth: 60 },
  { id: 'status', label: 'ステータス' },
  { id: 'pax', label: '人数' },
  { id: 'flight', label: 'ARR/DEP' },
  { id: 'location', label: '場所' },
  { id: 'license', label: '免許' },
  { id: 'payment', label: '支払' },
  { id: 'contact', label: '連絡' },
];

// 連絡手段マスタ
const COMMUNICATION_TOOLS = [
  { id: 1, label: 'TEL' },
  { id: 2, label: 'LINE' },
  { id: 3, label: 'SMS' },
  { id: 4, label: 'WhatsApp' },
  { id: 5, label: 'Instagram' },
  { id: 6, label: 'その他' },
];

// 行内チップ用の短縮表記（2026-08-25）。他のラベルと同じ固定幅に収めるため。
// 全名はチップの Tooltip とサイドパネル・変更モーダルで見えるので情報は失われない。
const COMMUNICATION_SHORT_LABELS: Record<string, string> = {
  WhatsApp: 'WA',
  Instagram: 'IG',
};
const shortToolLabel = (label: string): string => COMMUNICATION_SHORT_LABELS[label] || label;

// 省略表示ロジック（truncStatusName / shortVehicleCode）は src/lib/displayFormat.ts に共有化。
// モバイルPWA（MobileReservationCard / MobileVehicleCard）と同一の表記を維持すること。


export default function ReservationTable({
  title,
  data,
  type,
  vehicles,
  formatTime,
  onVehicleClick,
  onStatusClick,
  onLocationStatusClick,
  onReservationSave,
  onQuickUpdate,
  onStaffClick,
  onLicenseToggle,
  onCommunicationToolsUpdate,
  squarePaymentCache = {},
  squarePaymentLoading = false,
  onPaymentCheck,
  // 車両ポジションの該当車両カードへジャンプ（2026-08-10 追加）
  onLocateVehicle,
  // 外部（車両ポジション等）から特定予約のサイドパネルを開く要求（2026-08-10 追加）
  openReservationRequest,
  onBeforeOpenReservation, // 予約パネルを開く前に呼ばれるコールバック（trueを返すと開く、falseで中止）
  forceCloseReservationPanel, // 外部から予約パネルを閉じるためのフラグ
  reservationStatusList = [], // 動的に取得した予約ステータスリスト
  vehicleStatusList = [], // 動的に取得した車両ステータスリスト
  locationStatusList = [], // 動的に取得した車両移動ステータスリスト
  staffList = [], // スタッフリスト
  onCheckUpdate, // 車両チェック項目更新コールバック
  upcomingStatuses = {}, // 未来の予約状況
  allowCopy = true, // コピー機能を許可するかどうか
  onAssignClick, // 車両アサインクリックハンドラ
  carTypesList = [], // 車種リスト
  isSimpleView = false, // シンプル表示モード
  isToday = false, // 本日の日付を表示しているか
  onHoverVehicle, // ホバー時のコールバック
  onLeaveVehicle, // ホバー解除時のコールバック
  lineUsers = [], // LINEユーザー情報
  onOpenLineChat, // LINEチャットモーダルを開くコールバック
  onOpenResvChat = null, // 自社チャット（予約チャット）を開く: (sessionKey, reservationId) => void。無ければアイコンを出さない（2026-10-30）
  hideSearch = false, // 検索フィールドを非表示にする
  onContactClick, // 「連絡」ボタンクリック
  hideToolbar = false, // ツールバー（タイトル+検索+設定）を非表示にする
  compactMode = false, // コンパクト表示（15件でスクロール）
  externalSearchTerm, // 外部から検索語を受け取る（hideToolbar 時）
  onSettingsOpen, // 外部から設定ボタンを制御
  onVehicleSwap = null, // 車両DnDスワップ: (sourceRow, targetRow) => void
  vehicleDragSource = null, // 外部管理のドラッグソース予約
  onVehicleDragSourceChange = null, // ドラッグソース変更コールバック: (row | null) => void
  flightInfoMap = null, // 那覇FIS照合結果: { [reservationId]: { arrival, matchedVia } }（貸出リストのみ）
  mergedView = false, // 貸出・返却の統合リスト表示（30分グリッド + レーン + ターンアラウンド連結）
}) {
  const theme = useTheme();
  // 顧客DB由来の印（★=リピーター / ⧉=同日複数台）。取得は一括1回・キャッシュ付き
  const { markOf } = useCustomerFlags();

  // LINE 無効店舗(宮古)では 💬 吹き出しを出さない。
  // LINE API は那覇にしか無いため lineUsers は常に空で、吹き出しは永久にグレー
  // (=「LINE未連携」)のまま押しても何も起きない死んだ UI になる。
  // App.tsx / Dashboard / MobilePage の LINE メニューと同じ基準で揃える。
  const lineEnabled = isLineEnabled();

  // 予約 ID → 予約チャット（LINE ページと共有の一覧。取得は親の daylist が行う・customer-chat.md §13-7）
  const { sessions: resvChatSessions } = useReservationChat();
  const resvChatMap = useMemo(() => (onOpenResvChat ? reservationChatByReservation(resvChatSessions) : {}), [resvChatSessions, onOpenResvChat]);
  /** 予約チャットがある予約だけに出すアイコン（LINE と同じ吹き出し・色は青で LINE の緑と見分ける）。無い予約には出さない */
  const renderResvChatIcon = (row, fontSize = '0.9rem') => {
    const sess = resvChatMap[String(row.id)];
    if (!sess || !onOpenResvChat) return null;
    const unread = isSessionUnreadForStaff(sess);
    return (
      <Tooltip title={unread ? '自社チャット（新着あり）を開く' : '自社チャット（予約チャット）を開く'} arrow>
        <Box
          component="span"
          data-testid="resv-chat-icon"
          onClick={(e) => { e.stopPropagation(); onOpenResvChat(sess.session_key, row.id); }}
          sx={{ position: 'relative', display: 'inline-flex', cursor: 'pointer', flexShrink: 0, lineHeight: 1, '&:hover': { transform: 'scale(1.15)' }, transition: 'transform 0.1s' }}
        >
          <ChatBubbleIcon data-testid="resv-chat-bubble" sx={{ fontSize, color: RESV_CHAT_COLOR, display: 'block' }} />
          {unread && (
            <Box sx={{ position: 'absolute', top: -2, right: -3, width: 7, height: 7, bgcolor: '#ff4d4d', borderRadius: '50%', border: '1px solid #fff' }} />
          )}
        </Box>
      </Tooltip>
    );
  };

  // Create lookup map for LINE linked reservations (reservation_id → LINE user)
  const lineUserMap = useMemo(() => {
    const map = {};
    if (Array.isArray(lineUsers)) {
      lineUsers.forEach(u => {
        // Map each linked reservation_id to this LINE user
        const rids = u.reservation_ids || [];
        rids.forEach(rid => {
          if (!String(rid).startsWith('STAFF:')) {
            map[String(rid)] = u;
          }
        });
        // Also support legacy system_user_id mapping
        if (u.system_user_id) {
          map[String(u.system_user_id)] = u;
        }
      });
    }
    return map;
  }, [lineUsers]);
  // Ensure same breakpoint as DayListReservations
  const isMobile = useMediaQuery('(max-width:800px)');
  const ALL_COLUMNS = React.useMemo(() => getColumns(isMobile), [isMobile]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [resStatusModal, setResStatusModal] = useState({ open: false, row: null });

  // propsが渡されない場合はデフォルト値を使用
  const statusOptions = useMemo(() => {
    return reservationStatusList?.length > 0 ? reservationStatusList : DEFAULT_RESERVATION_STATUS_LIST;
  }, [reservationStatusList]);

  // ステータスIDからコードへのマッピング（動的に生成）
  const statusIdToCode = useMemo(() => {
    const map = {};
    statusOptions.forEach(s => {
      if (s.id !== undefined && s.code) map[s.id] = s.code;
    });
    return map;
  }, [statusOptions]);

  // ステータスコードからIDへのマッピング（動的に生成）
  const statusCodeToId = useMemo(() => {
    const map = {};
    statusOptions.forEach(s => {
      if (s.id !== undefined && s.code) map[s.code] = s.id;
    });
    return map;
  }, [statusOptions]);

  // 車両ステータス id → code（対応中判定に使う。id は店舗ごとに違うので code で判定する）
  const vehicleStatusIdToCode = useMemo(() => {
    const map: Record<string, string> = {};
    vehicleStatusList.forEach(s => {
      if (s.id !== undefined && s.code) map[String(s.id)] = s.code;
    });
    return map;
  }, [vehicleStatusList]);

  // 車両ステータスコードからIDへのマッピング
  const vehicleStatusCodeToId = useMemo(() => {
    const map = {};
    vehicleStatusList.forEach(s => {
      if (s.id !== undefined && s.code) map[s.code] = s.id;
    });
    return map;
  }, [vehicleStatusList]);

  // Extract vehicleTypes from vehicles list for Editor
  const vehicleTypes = useMemo(() => {
    if (!vehicles || typeof vehicles !== 'object') return [];
    const vehicleList = Object.values(vehicles);
    const types = [];
    const seen = new Set();
    vehicleList.forEach((v: any) => {
      if (v.vehicle_type) {
        // Use ID or Code as unique key
        const key = v.vehicle_type.id;
        if (!seen.has(key)) {
          seen.add(key);
          types.push(v.vehicle_type);
        }
      }
    });
    return types;
  }, [vehicles]);

  // 外部からパネルを閉じる
  useEffect(() => {
    if (forceCloseReservationPanel) {
      setSelectedReservation(null);
    }
  }, [forceCloseReservationPanel]);

  // 予約パネルを開く処理
  // 外部からの「この予約を開いて」要求。自分の data に含まれる場合だけ開く
  // （貸出/返却で表が分かれているため、該当する側のパネルだけが開く）
  useEffect(() => {
    if (!openReservationRequest?.id) return;
    const row = (data || []).find((r) => String(r.id) === String(openReservationRequest.id));
    if (row) setSelectedReservation(row);
    // nonce を見て、同じ予約を続けて指定された場合も再度開く
  }, [openReservationRequest?.nonce, openReservationRequest?.id, data]);

  const handleOpenReservation = (row) => {
    if (onBeforeOpenReservation) {
      const canOpen = onBeforeOpenReservation(row);
      if (!canOpen) return;
    }
    setSelectedReservation(row);
  };

  // 予約ステータス変更ハンドラ
  const handleResStatusUpdate = (statusId) => {
    if (resStatusModal.row) {
      // パネルを開く。ステータスを変更した状態で。
      const updated = { ...resStatusModal.row, status: statusId };
      setSelectedReservation(updated);
      setResStatusModal({ open: false, row: null });
    }
  };

  // Load initial state from localStorage
  const getInitialVisibleColumns = () => {
    // デフォルトは全列表示
    const defaultColumns = ALL_COLUMNS.reduce((acc, col) => ({ ...acc, [col.id]: true }), {});
    try {
      const saved = localStorage.getItem(`reservation_table_visible_${type}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        // 保存された設定と新規追加された列をマージ（新規列はデフォルトで表示）
        return { ...defaultColumns, ...parsed };
      }
    } catch (e) {
      console.error('Failed to load settings', e);
    }
    return defaultColumns;
  };

  const getInitialSelectedStatuses = () => {
    try {
      const saved = localStorage.getItem(`reservation_table_status_${type}`);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to load settings', e);
    }
    return DEFAULT_RESERVATION_STATUS_LIST.reduce((acc, status) => ({ ...acc, [status.id]: true }), {});
  };

  const getInitialSeatVisibility = () => {
    const defaultVisibility = { CS: true, BS: true, JS: true };
    try {
      const saved = localStorage.getItem(`reservation_table_seat_visibility_${type}`);
      if (saved) {
        return { ...defaultVisibility, ...JSON.parse(saved) };
      }
    } catch (e) {
      console.error('Failed to load seat visibility settings', e);
    }
    return defaultVisibility;
  };

  const [expandedRows, setExpandedRows] = useState({});

  const toggleRowExpansion = (rowId) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  };

  // 行の展開状態（クリックでピン留め）。デフォルトは全行コンパクト1行表示
  const [expandedDoneRows, setExpandedDoneRows] = useState({});
  const toggleDoneRowExpansion = (rowId) => {
    setExpandedDoneRows(prev => ({ ...prev, [rowId]: !prev[rowId] }));
  };
  // hover中の行（150msディレイで詳細カードを展開）

  const [visibleColumns, setVisibleColumns] = useState(getInitialVisibleColumns);
  const [selectedStatuses, setSelectedStatuses] = useState(getInitialSelectedStatuses);
  // 保存済み設定があるか（無い場合のみ、マスタ到着後に既定チェックを適用する）
  const [statusDefaultsApplied, setStatusDefaultsApplied] = useState(() => {
    try { return !!localStorage.getItem(`reservation_table_status_${type}`); } catch { return true; }
  });
  const [seatVisibility, setSeatVisibility] = useState(getInitialSeatVisibility);
  const [editingReservation, setEditingReservation] = useState(null);

  const [isSimpleViewInternal, setIsSimpleViewInternal] = useState(false); // Removed local state if prop is used, but keeping it for now if needed. 
  // Wait, let's use the prop directly.
  const actualVisibleColumns = isSimpleView
    ? { vehicle: true }
    : visibleColumns;

  // 連絡手段モーダル用state
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactModalReservation, setContactModalReservation] = useState(null);
  const [selectedTools, setSelectedTools] = useState([]);

  // 車両チェックモーダル用state
  const [checkModalOpen, setCheckModalOpen] = useState(false);
  const [checkModalVehicle, setCheckModalVehicle] = useState(null);
  const [checkModalItems, setCheckModalItems] = useState([]);
  const [searchTermInternal, setSearchTermInternal] = useState('');
  const searchTerm = externalSearchTerm !== undefined ? externalSearchTerm : searchTermInternal;
  const setSearchTerm = externalSearchTerm !== undefined ? (() => {}) : setSearchTermInternal;

  // ショートメモ・時間更新モーダル用
  const [shortMemoModal, setShortMemoModal] = useState({ open: false, row: null, value: '' });
  const [requestTimeModal, setRequestTimeModal] = useState({ open: false, row: null, value: '', field: '' });

  useEffect(() => {
    if (selectedReservation) {
      // 共通ユーティリティでエディタ用データに変換
      setEditingReservation(toEditorReservation(selectedReservation));
    } else {
      setEditingReservation(null);
    }
  }, [selectedReservation]);

  const handleCopyReservation = (row) => {
    // 既存の予約をベースに新規予約オブジェクトを作成
    const currentCode = row.contact_code || '';
    const baseCode = currentCode.replace(/-(\d+)$/, '');

    // 既存の取得可能な全データから最大のスフィックスを計算
    const cachedData = reservationSyncService.getReservations();
    const allKnownReservations = [...(Array.isArray(data) ? data : []), ...cachedData];

    let maxSuffix = 1;
    if (baseCode) {
      const pattern = new RegExp(`^${baseCode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:-(\\d+))?$`);
      allKnownReservations.forEach(r => {
        const code = r.contact_code;
        if (code) {
          const match = code.match(pattern);
          if (match) {
            const suffix = match[1] ? parseInt(match[1], 10) : 1;
            if (suffix > maxSuffix) maxSuffix = suffix;
          }
        }
      });
    }

    const nextCode = baseCode ? `${baseCode}-${maxSuffix + 1}` : '';

    const copied = {
      ...row,
      id: null, // IDをnullにすることで新規扱いにする
      contact_code: nextCode,
      __isNew: true, // 新規予約フラグ
      __isCopy: true // コピーフラグ
    };
    // パネルを開く
    setSelectedReservation(copied);
  };

  // Save to localStorage on change
  useEffect(() => {
    localStorage.setItem(`reservation_table_visible_${type}`, JSON.stringify(visibleColumns));
  }, [visibleColumns, type]);

  // 初回（localStorage 未保存）のみ、ステータスマスタ到着後に既定チェックを適用する。
  // フィルタは `selectedStatuses[id] === false` で除外する実装のため、
  // 既定で隠したいステータスには明示的に false を入れる必要がある。
  useEffect(() => {
    if (statusDefaultsApplied) return;
    if (!Array.isArray(reservationStatusList) || reservationStatusList.length === 0) return;
    const defaults = {};
    reservationStatusList.forEach((s) => {
      if (s?.id == null) return;
      defaults[s.id] = DEFAULT_VISIBLE_STATUS_CODES.includes(s.code);
    });
    setSelectedStatuses(defaults);
    setStatusDefaultsApplied(true);
  }, [statusDefaultsApplied, reservationStatusList]);

  useEffect(() => {
    // 既定適用前の暫定値（空オブジェクト）を保存してしまうと、次回以降
    // 「保存済み設定あり」と判定され既定が二度と適用されなくなるため待つ
    if (!statusDefaultsApplied) return;
    localStorage.setItem(`reservation_table_status_${type}`, JSON.stringify(selectedStatuses));
  }, [selectedStatuses, type, statusDefaultsApplied]);

  useEffect(() => {
    localStorage.setItem(`reservation_table_seat_visibility_${type}`, JSON.stringify(seatVisibility));
  }, [seatVisibility, type]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleColumnToggle = (columnId) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  const handleStatusToggle = (statusId) => {
    setSelectedStatuses((prev) => {
      // 現在の状態を取得（undefinedの場合はデフォルトでtrueとする）
      const currentState = prev[statusId] !== false;
      return {
        ...prev,
        [statusId]: !currentState,
      };
    });
  };

  const handleSeatVisibilityToggle = (seatType) => {
    setSeatVisibility((prev) => ({
      ...prev,
      [seatType]: !prev[seatType],
    }));
  };

  const getColumnLabel = (col) => {
    if (col.id === 'flight') {
      return type === 'departure' ? 'ARR' : 'DEP';
    }
    if (col.id === 'pax') {
      const seatLabels = [];
      if (seatVisibility.CS) seatLabels.push('C');
      if (seatVisibility.BS) seatLabels.push('B');
      if (seatVisibility.JS) seatLabels.push('J');
      return <div><div>人数</div>{seatLabels.length > 0 && <div style={{ fontSize: '0.75rem' }}>{seatLabels.join('/')}</div>}</div>;
    }
    return col.label;
  };

  const filteredData = useMemo(() => {
    return data.filter((row) => {
      // 1. Status Filter
      // ステータスIDまたはコードをIDに正規化
      let sId = row.status;
      if (typeof sId === 'string' && isNaN(Number(sId))) {
        sId = statusCodeToId[sId] || sId;
      }

      if (selectedStatuses[sId] === false) {
        return false;
      }

      // 2. Search Filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const vehicle = vehicles[row.vehicle_id] ?? {};

        // Search Targets
        const searchTargets = [
          row.customer_name,
          row.last_name,
          row.first_name,
          row.last_name_kana,
          row.first_name_kana,
          vehicle.name,
          vehicle.vehicle_code,
          vehicle.car_type,
          row.reservation_number, // data dependant
          String(row.id)
        ];

        const match = searchTargets.some(val =>
          val && String(val).toLowerCase().includes(term)
        );
        if (!match) return false;
      }

      return true;
    });
  }, [data, selectedStatuses, searchTerm, vehicles, reservationStatusList, statusCodeToId]);

  return (
    <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {!hideToolbar && (<><Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
          {title} ({filteredData.length})
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {!hideSearch && (
            <TextField
              size="small"
              placeholder="検索"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mr: 1, minWidth: 150 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
            />
          )}
          <IconButton onClick={handleMenuOpen} size="small">
            <SettingsIcon />
          </IconButton>
        </Box>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            style: {
              maxHeight: 400,
              width: '25ch',
            },
          }}
        >
          <ListSubheader>表示項目</ListSubheader>
          {ALL_COLUMNS.map((col) => (
            <MenuItem key={col.id} dense>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={visibleColumns[col.id]}
                    onChange={() => handleColumnToggle(col.id)}
                    size="small"
                  />
                }
                label={getColumnLabel(col)}
              />
            </MenuItem>
          ))}
          <Divider />
          <ListSubheader>シート表示 (C/B/J)</ListSubheader>
          {['CS', 'BS', 'JS'].map((seatType) => (
            <MenuItem key={seatType} dense>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={seatVisibility[seatType]}
                    onChange={() => handleSeatVisibilityToggle(seatType)}
                    size="small"
                  />
                }
                label={seatType === 'CS' ? 'チャイルドシート (C)' : seatType === 'BS' ? 'ブースターシート (B)' : 'ジュニアシート (J)'}
              />
            </MenuItem>
          ))}
          <Divider />
          <ListSubheader>ステータスを選択</ListSubheader>
          {statusOptions.map((status) => (
            <MenuItem key={status.id} dense>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedStatuses[status.id] !== false}
                    onChange={() => handleStatusToggle(status.id)}
                    size="small"
                  />
                }
                label={status.label}
              />
            </MenuItem>
          ))}
        </Menu>
      </Box></>)}

      <TableContainer sx={{
        maxHeight: compactMode ? 540 : 'none',
        overflowY: compactMode ? 'auto' : 'visible',
        // コンパクト1行+折り返しレイアウトのため横スクロールはさせない。
        // 2026-07-27: hidden → clip に変更。CSS 仕様上 overflow-x:hidden は
        // overflow-y:visible を auto に強制するため、非コンパクトでも縦スクロールバーが
        // 出てしまっていた。clip はスクロールコンテナを作らないのでバーが出ない。
        overflowX: 'clip',
        // コンパクト時の縦スクロールもバー非表示（ホイール/ドラッグでのスクロールは可能）
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        border: '1px solid #ddd',
        borderRadius: 1
      }}>
        <Table stickyHeader size="small" sx={{ minWidth: 'auto', borderCollapse: 'separate', borderSpacing: 0 }}>
          <TableHead>
            <TableRow>
              {isSimpleView ? (
                ALL_COLUMNS.map((col) => (
                  actualVisibleColumns[col.id] && (
                    <TableCell
                      key={col.id}
                      sx={{
                        whiteSpace: col.id === 'time' ? 'normal' : 'nowrap',
                        fontWeight: 'bold',
                        backgroundColor: type === 'return' ? '#424242' : '#f5f5f5',
                        color: type === 'return' ? '#ffffff' : 'inherit',
                        borderRight: 'none',
                        borderLeft: 'none',
                        position: 'sticky',
                        ...(col.sticky && {
                          left: col.left,
                          zIndex: 3,
                          minWidth: col.minWidth,
                          boxShadow: col.id === 'time' ? '2px 0 4px rgba(0,0,0,0.1)' : 'none',
                        })
                      }}
                    >
                      {getColumnLabel(col)}
                    </TableCell>
                  )
                ))
              ) : (
                <TableCell
                  sx={{
                    py: 0.4,
                    px: 1,
                    fontWeight: 'bold',
                    fontSize: '0.7rem',
                    backgroundColor: type === 'return' ? '#424242' : '#f5f5f5',
                    color: type === 'return' ? '#ffffff' : UI.color.textSub,
                  }}
                >
                  時間 / 名前 / 車両 / 予約状態　（クリックで詳細）
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((row) => {
              // 統合表示（mergedView）では行ごとに departure/return が混在する
              const rowType = row.__listType || type;
              const vehicle = vehicles[row.vehicle_id];
              // 車両ステータス情報の取得（IDまたはコードで検索）
              const vStatus = vehicle?.status;
              const statusInfo = vehicleStatusList?.find(s =>
                s.id === Number(vStatus) || (typeof vStatus === 'string' && s.code === vStatus)
              );
              const time = rowType === 'departure' ? row.start_date : row.end_date;
              // NOTE: Kept for safety even though data is validated
              const detail = row.detail || {};
              const flight = rowType === 'departure' ? (row.arrival_flight || detail.arrival_flight) : (row.departure_flight || detail.departure_flight);
              const pax = row.pax || detail.pax;
              const name = row.customer_name || `${row.last_name} ${row.first_name}`;
              const displayName = name.length > 10 ? name.substring(0, 10) + '...' : name;
              // 名前列の背景色 → 予約自身が希望する車両タイプ（row.car_type）の色
              // 車両列の背景色 → 実際にアサインされた車両（vehicle.vehicle_type）の色
              // 実車両のタイプが予約の希望と異なる場合に色違いで可視化できる。
              // 2026-09-24: row.car_type にはコード('T-AV-H')だけでなく名前('アルファード30系')が
              // 入っている予約が多数あるため、コード→名前→id の順で寛容に突き合わせる（findCarType）。
              const reservationCarType = findCarType(carTypesList, row.car_type);
              const assignedCarType = findCarType(
                carTypesList,
                vehicle?.vehicle_type?.vehicle_type_code || vehicle?.vehicle_type?.code || vehicle?.car_type,
              );

              // 予約ステータス情報の取得
              const rStatus = row.status;
              const statusOpt = statusOptions.find(o =>
                o.id === Number(rStatus) || (typeof rStatus === 'string' && o.code === rStatus)
              );
              const statusName = row.status_name || (statusOpt ? statusOpt.label : '');

              // 予約が「処理済み（対応不要）」かどうか判定
              // 貸出リスト：利用中（出発済み）
              // 返却リスト：返却済み
              const isProcessedRes = (
                (rowType === 'departure' && (
                  rStatus === 3 ||
                  rStatus === '3' ||
                  rStatus === 4 ||
                  rStatus === '4' ||
                  statusName.includes('利用中') ||
                  statusName.includes('出発中') ||
                  statusName.includes('貸出済み') ||
                  statusName.includes('貸出済') ||
                  (statusOpt && (
                    statusOpt.label?.includes('利用中') ||
                    statusOpt.label?.includes('出発中') ||
                    statusOpt.label?.includes('貸出済み') ||
                    statusOpt.label?.includes('貸出済') ||
                    statusOpt.code === 'in_use' ||
                    statusOpt.code === 'done'
                  ))
                )) ||
                (rowType === 'return' && (
                  rStatus === 4 ||
                  rStatus === '4' ||
                  statusName.includes('貸出済み') ||
                  statusName.includes('貸出済') ||
                  (statusOpt && (
                    statusOpt.label?.includes('貸出済み') ||
                    statusOpt.label?.includes('貸出済') ||
                    statusOpt.code === 'done'
                  ))
                ))
              );

              // 完了行（当日の処理済み）: ✓マーク + グレー背景で表現
              const isDoneRow = isProcessedRes && isToday;

              // 対応中（黄色点滅）: 貸出=車が迎えに向かっている / 返却=車が戻ってきて手続き中
              // 当日表示のときだけ。過去日・未来日は「今動いている」状態ではない
              // 貸出行は「連絡」（直接来店以外）が入っていても対応中（2026-09-19）
              const isInProgressRow = isToday && !isDoneRow && isReservationInProgress({
                rowType,
                vehicleStatusCode: vehicle ? (vehicleStatusIdToCode[String(vehicle.status)] || vehicle.status_code || null) : null,
                reservationStatus: statusOpt?.code ?? row.status,
                arrivalContact: rowType === 'departure' ? getArrivalContact(row) : null,
                isUsMilitary: isUsMilitaryReservation(row),
              });

              // フライト見込み時間による時刻色分け:
              //   見込みが分かる予約 = 濃青 / 見込み時間を過ぎてまだ利用中になっていない = 赤
              const fmRow = (rowType === 'departure' && flightInfoMap) ? flightInfoMap[String(row.id)] : null;
              const fmEst = fmRow ? (fmRow.arrival.estimated || fmRow.arrival.actual || fmRow.arrival.scheduled) : null;
              const fmOverdue = !!(
                fmEst && isToday && !isProcessedRes &&
                statusOpt?.code !== 'cancelled' &&
                dayjs().format('HH:mm') > fmEst
              );
              const compactTimeColor = fmOverdue
                ? '#dc2626'
                : (fmRow ? '#1d4ed8' : (isDoneRow ? UI.color.textSub : UI.color.text));

              // FIS のステータス文言（降機中 / 手荷物受取中 / 欠航 等）と色
              const fmStatusText = fmRow ? (fmRow.arrival.statusJp || fmRow.arrival.statusEn || '') : '';
              const fmStatusColor = fmRow ? flightStatusColor(fmRow.arrival) : { bg: '#eee', text: '#333' };

              // 予約データに個別の背景色設定がある場合はそれを最優先
              const hasResColor = row.bg_color && typeof row.bg_color === 'string' && row.bg_color.trim();

              // 米軍予約は予約名を専用色にして他の予約と区別する（2026-09-24。手動色より優先。車両コードは車種色のまま）
              const isUsMil = isUsMilitaryReservation(row);

              // 名前列のタグスタイル: 予約自身の car_type 由来の色（row.bg_color の手動上書きが最優先）
              const tagStyle = isUsMil ? {
                backgroundColor: US_MILITARY_COLORS.bg,
                color: US_MILITARY_COLORS.text,
                display: 'inline-block', padding: '0.1em 0.3em', borderRadius: '3px', lineHeight: '1.2'
              } : hasResColor ? {
                backgroundColor: row.bg_color.trim(),
                color: (row.text_color && typeof row.text_color === 'string' && row.text_color.trim()) ? row.text_color.trim() : '#000',
                display: 'inline-block', padding: '0.1em 0.3em', borderRadius: '3px', lineHeight: '1.2'
              } : {
                backgroundColor: reservationCarType?.bg_color || '#eee',
                color: reservationCarType?.text_color || '#000',
                display: 'inline-block', padding: '0.1em 0.3em', borderRadius: '3px', lineHeight: '1.2'
              };

              // 車両列のスタイル: アサインされた車両の vehicle_type 由来の色（予約の手動色は無視）
              const vehicleOriginalStyle = vehicle ? {
                backgroundColor: assignedCarType?.bg_color || vehicle.vehicle_type?.bg_color || vehicle.bg_color || '#eee',
                color: assignedCarType?.text_color || vehicle.vehicle_type?.text_color || vehicle.text_color || '#000',
                display: 'inline-block', padding: '0.1em 0.3em', borderRadius: '3px', lineHeight: '1.2'
              } : {
                backgroundColor: '#eee',
                color: '#000',
                display: 'inline-block', padding: '0.1em 0.3em', borderRadius: '3px', lineHeight: '1.2'
              };

              // スタッフ情報を取得
              // 貸出: pu_staff（運転者）, rent_staff（対応者）
              // 返却: do_staff（運転者）, return_staff（対応者）
              const driverStaffRaw = rowType === 'departure' ? row.pu_staff : row.do_staff;
              const handlerStaffRaw = rowType === 'departure' ? row.rent_staff : row.return_staff;

              let driverStaff = (driverStaffRaw && Array.isArray(staffList)) ? (staffList.find(s => s.id === driverStaffRaw.id) || driverStaffRaw) : driverStaffRaw;
              const handlerStaff = (handlerStaffRaw && Array.isArray(staffList)) ? (staffList.find(s => s.id === handlerStaffRaw.id) || handlerStaffRaw) : handlerStaffRaw;

              // ID:14（スポットスタッフ）の場合: メモからアサイン名を取得（見つかった場合のみ上書き）
              if (driverStaff && isSpotStaff(driverStaff)) {
                const memo = row.detail?.memo || row.memo || '';
                const assignLabel = rowType === 'departure' ? '貸出運転者アサイン' : '返却運転者アサイン';
                const memoRegex = new RegExp(`---${assignLabel} [\\d-]+ [\\d:]+---\\n(.+?)\\n---`);
                const match = memo.match(memoRegex);
                if (match) {
                  driverStaff = { ...driverStaff, staff_name: match[1], name: match[1] };
                }
              }

              const contactPlan = row.contact_plan || detail.contact_plan || '';
              // 🏨ホテル配車（優先）/ 🚢クルーズ船 / 🏪駅近来店 / 👤代表者送迎 / 🚶‍♀️セルフ / 🚃ステーション（判定は utils/planPrefix.ts。2026-10-25 🏨・2026-11-02 🚢 追加）
              const hotelMark = hotelDeliveryMarkOf(row.json_data || detail.json_data, rowType === 'return' ? 'return' : 'departure');
              const cruiseMark = cruiseMarkOf(row.json_data || detail.json_data, row.comment || detail.comment);
              const planPrefix = rowPrefixOf(contactPlan, hotelMark, cruiseMark);
              const planPrefixTitle = rowPrefixTitleOf(contactPlan, hotelMark, cruiseMark);
              // ホテル配車は濃い青緑・クルーズ船は濃い青の点線の枠（outline なので行の大きさは変わらない）
              const rowOutline = rowOutlineOf(hotelMark, cruiseMark);
              // 配車場所が未入力なら 🏨 の右上に赤い点（⚠ はショートメモの印なので使わない）
              const hotelDotSx = hotelMark?.placeMissing
                ? { position: 'relative' as const, '&::after': { content: '""', position: 'absolute', top: -2, right: -2, width: 7, height: 7, borderRadius: '50%', backgroundColor: '#dc2626', border: '1px solid #fff' } }
                : {};

              const isAgreement = Number(detail.agreement) === 1 || detail.agreement === true;
              const isLicense = Number(detail.license) === 1 || detail.license === true;
              const isPayment = Number(detail.payment) === 1 || detail.payment === true;

              const checkCount = (isAgreement ? 1 : 0) + (isLicense ? 1 : 0) + (isPayment ? 1 : 0);
              let borderLeftColor = '#7e7e7e';
              if (checkCount === 3) {
                borderLeftColor = '#00ac00';
              } else if (checkCount > 0) {
                borderLeftColor = '#ffe747';
              }

              const isExpanded = expandedRows[row.id];


              // --- 共通コンポーネントの定義 ---
              const agreementIcon = (
                <Tooltip title={`事前受付: ${isAgreement ? '完了' : '未完了'}`} arrow>
                  <span style={{ fontSize: '0.85rem', lineHeight: 1, opacity: isAgreement ? 1 : 0.15, paddingBottom: '1px', filter: isAgreement ? 'none' : 'grayscale(1)' }}>📄</span>
                </Tooltip>
              );
              const licenseIcon = (
                <Tooltip title={`免許証: ${isLicense ? '完了' : '未完了'}`} arrow>
                  <span style={{ fontSize: '0.85rem', lineHeight: 1, opacity: isLicense ? 1 : 0.15, paddingBottom: '1px', filter: isLicense ? 'none' : 'grayscale(1)' }}>🪪</span>
                </Tooltip>
              );
              const isPkg = (
                (detail.contact_media && String(detail.contact_media).toUpperCase().includes('PKG')) ||
                (detail.plan_name && String(detail.plan_name).toUpperCase().includes('PKG')) ||
                (row.plan_name && String(row.plan_name).toUpperCase().includes('PKG'))
              );
              // CarMore 経由の予約はプラットフォーム側で支払い済みのため確認不要（2026-07-26）
              const isCarmore = (
                (detail.contact_media && String(detail.contact_media).toLowerCase().includes('carmore')) ||
                (row.contact_media && String(row.contact_media).toLowerCase().includes('carmore'))
              );

              const paymentIcon = (
                <Tooltip title={(!isPayment && isCarmore) ? 'CarMore経由（支払い済み・確認不要）' : (!isPayment && isPkg) ? "パッケージ" : `支払い: ${isPayment ? '完了' : '未完了'}`} arrow>
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onPaymentCheck) onPaymentCheck(row);
                    }}
                    style={{
                      fontSize: '0.85rem',
                      lineHeight: 1,
                      opacity: (isPayment || isPkg || isCarmore) ? 1 : 0.15,
                      filter: (isPayment || isPkg || isCarmore) ? 'none' : 'grayscale(1)',
                      display: 'inline-block',
                      width: '1em',
                      textAlign: 'center',
                      fontWeight: 'bold',
                      color: (!isPayment && isCarmore) ? '#0d9488' : (!isPayment && isPkg) ? '#1976d2' : 'inherit',
                      cursor: onPaymentCheck ? 'pointer' : 'default'
                    }}
                  >
                    {(!isPayment && isCarmore) ? 'C' : (!isPayment && isPkg) ? 'P' : '💰'}
                  </span>
                </Tooltip>
              );

              const lineUserData = lineUserMap[String(row.id)];

              const nameSection = (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => { e.stopPropagation(); handleOpenReservation(row); }}>
                  {lineEnabled && (
                  <Tooltip title={lineUserData ? (lineUserData.unread_count > 0 ? `LINE (未読 ${lineUserData.unread_count}件)` : 'LINEチャットを開く') : 'LINE未連携'} arrow>
                    <Box
                      onClick={(e) => { e.stopPropagation(); if (lineUserData && onOpenLineChat) onOpenLineChat(lineUserData, row.id); }}
                      sx={{
                        position: 'relative', display: 'flex', alignItems: 'center',
                        cursor: lineUserData ? 'pointer' : 'default',
                        '&:hover': lineUserData ? { transform: 'scale(1.15)' } : {},
                        transition: 'transform 0.1s',
                        opacity: lineUserData ? 1 : 0.25,
                      }}
                    >
                      <ChatBubbleIcon data-testid="line-chat-bubble" sx={{
                        fontSize: '1.2rem',
                        display: 'block',
                        color: lineUserData ? LINE_CHAT_COLOR : CHAT_ICON_DISABLED_COLOR,
                        filter: lineUserData?.unread_count > 0 ? `drop-shadow(0 0 2px ${LINE_CHAT_COLOR})` : 'none'
                      }} />
                      {lineUserData?.unread_count > 0 && (
                        <Box sx={{
                          position: 'absolute',
                          top: -2,
                          right: -4,
                          width: 8,
                          height: 8,
                          bgcolor: '#ff4d4d',
                          borderRadius: '50%',
                          border: '1px solid #fff'
                        }} />
                      )}
                    </Box>
                  </Tooltip>
                  )}
                  {renderResvChatIcon(row, '1.1rem')}
                  {/* 顧客DB由来の印（★=リピーター / ⧉=同日複数台）。正: md/customers.md */}
                  <CustomerMarkIcon mark={markOf(row.contact_code)} size={13} />
                  <Tooltip title={name} arrow><span style={{ ...tagStyle, fontWeight: 'bold' }}>{displayName}</span></Tooltip>
                  {planPrefix && <Tooltip title={planPrefixTitle || ''} arrow><Box component="span" data-testid="plan-prefix" sx={hotelDotSx}>{planPrefix}</Box></Tooltip>}
                  {row.short_memo && (
                    <Tooltip title={row.short_memo} arrow>
                      <span
                        onClick={(e) => { e.stopPropagation(); setShortMemoModal({ open: true, row: row, value: row.short_memo || '' }); }}
                        style={{ cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1 }}
                      >⚠️</span>
                    </Tooltip>
                  )}
                  {allowCopy && (
                    <Tooltip title="コピー" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleCopyReservation(row); }}
                        sx={{ ml: 0.5, p: 0, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                      >
                        <ContentCopyIcon sx={{ fontSize: '0.9rem' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              );

              // 連絡ボタンのラベルと色。
              // 貸出行 = 到着連絡 + 空港サブゾーン / 返却行 = 返却連絡 + 返却場所。
              // 同じ予約が貸出日と返却日の両方でリストに出るので、キーを分けないと
              // 片方の設定がもう片方を上書きしてしまう（2026-08-24 分離）
              const contactJsonData = row.json_data || row.detail?.json_data;
              const contactArrivalKey = rowType === 'return' ? '返却連絡' : '到着連絡';
              const contactPlaceKey = rowType === 'return' ? '返却場所' : '空港';
              const hasContactArrival = contactJsonData?.data?.[contactArrivalKey] === '有り';
              const contactPlaceValue = contactJsonData?.data?.[contactPlaceKey];
              const matchedSubZone: any = rowType === 'return'
                ? RETURN_SUB_ZONES.find(sz => sz.value === contactPlaceValue)
                : PICKUP_SUB_ZONES.find(sz => sz.airportValue === contactPlaceValue);
              // こちらから電話連絡済み（空港=連絡済・到着連絡なし。車両ポジションの「連絡済み」サブエリア。2026-10-19）
              const isCalled = rowType !== 'return' && !hasContactArrival && contactPlaceValue === AIRPORT_CALLED;
              const contactLabel = hasContactArrival
                ? (contactPlaceValue && contactPlaceValue !== '未'
                  ? (contactPlaceValue === '直接来店' ? '直来' : contactPlaceValue)
                  : '連絡')
                : (isCalled ? AIRPORT_CALLED : '');
              const contactBgColor = (hasContactArrival && matchedSubZone && contactPlaceValue !== '未')
                ? matchedSubZone.borderColor : (hasContactArrival ? '#00695c' : (isCalled ? CALLED_COLOR : undefined));
              const contactTextColor = (hasContactArrival && matchedSubZone && matchedSubZone.textColor)
                ? matchedSubZone.textColor : '#fff';

              // 連絡（直来/空港サブゾーン）・運転者・対応者は個別要素として定義し、
              // 2段行レイアウト（連絡=予約状態の右 / 運転者・対応者=車両ステータスの下）と
              // 他ビューの staffButtons の両方から使う（2026-08-10 分解）
              const contactButtonEl = onContactClick ? (
                <Button
                  variant={(hasContactArrival || isCalled) ? "contained" : "outlined"}
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onContactClick(row, rowType); }}
                  sx={{
                    ...STAFF_BTN_SX,
                    ...((hasContactArrival || isCalled)
                      ? { backgroundColor: contactBgColor, color: contactTextColor, '&:hover': { backgroundColor: contactBgColor, filter: 'brightness(0.9)' } }
                      : { borderColor: '#bdbdbd', color: '#757575', '&:hover': { borderColor: '#9e9e9e' } }
                    ),
                  }}
                >
                  {/* 3文字幅の中で均等割り付け（DOM のテキストは「連絡」のままで検索できる） */}
                  <span style={{ display: 'block', width: '100%', textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <SpacedLabel label={truncStatusName(contactLabel || '連絡')} />
                  </span>
                </Button>
              ) : null;

              const driverHandlerButtons = (
                <>
                  <Button
                    variant={driverStaff?.bg_color ? "contained" : "outlined"}
                    size="small"
                    onClick={(e) => { e.stopPropagation(); onStaffClick && onStaffClick(row, 'driver', rowType); }}
                    sx={{
                      ...STAFF_BTN_SX,
                      backgroundColor: driverStaff?.bg_color || (driverStaff ? '#e8f5e9' : '#fff'),
                      borderColor: driverStaff?.text_color || (driverStaff ? '#4caf50' : '#bdbdbd'),
                      color: driverStaff?.text_color || (driverStaff ? '#2e7d32' : '#757575'),
                      '&:hover': { backgroundColor: driverStaff?.bg_color ? `${driverStaff.bg_color}dd` : (driverStaff ? '#c8e6c9' : '#f5f5f5'), borderColor: driverStaff?.text_color || (driverStaff ? '#4caf50' : '#9e9e9e'), filter: driverStaff?.bg_color ? 'brightness(0.95)' : 'none' }
                    }}
                  >
                    {/* 3文字幅に統一（タイミー→タイミ / 仲本は CSS で均等割り）。全名は title で補う */}
                    <span style={{ display: 'block', width: '100%', textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <SpacedLabel label={truncStatusName(driverStaff ? (driverStaff.staff_name || driverStaff.name || '') : '運転者')} />
                    </span>
                  </Button>
                  <Button
                    variant={handlerStaff?.bg_color ? "contained" : "outlined"}
                    size="small"
                    onClick={(e) => { e.stopPropagation(); onStaffClick && onStaffClick(row, 'handler', rowType); }}
                    sx={{
                      ...STAFF_BTN_SX,
                      backgroundColor: handlerStaff?.bg_color || (handlerStaff ? '#fff3e0' : '#fff'),
                      borderColor: handlerStaff?.text_color || (handlerStaff ? '#ff9800' : '#bdbdbd'),
                      color: handlerStaff?.text_color || (handlerStaff ? '#e65100' : '#757575'),
                      '&:hover': { backgroundColor: handlerStaff?.bg_color ? `${handlerStaff.bg_color}dd` : (handlerStaff ? '#ffe0b2' : '#f5f5f5'), borderColor: handlerStaff?.text_color || (handlerStaff ? '#ff9800' : '#9e9e9e'), filter: handlerStaff?.bg_color ? 'brightness(0.95)' : 'none' }
                    }}
                  >
                    <span style={{ display: 'block', width: '100%', textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <SpacedLabel label={truncStatusName(handlerStaff ? (handlerStaff.staff_name || handlerStaff.name || '') : '対応者')} />
                    </span>
                  </Button>
                </>
              );

              const staffButtons = (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}>
                  {contactButtonEl}
                  {driverHandlerButtons}
                </Box>
              );


              const isDragSource = vehicleDragSource && String(row.id) === String(vehicleDragSource.id);
              const isValidDropTarget = vehicleDragSource && String(row.id) !== String(vehicleDragSource.id) && vehicle;
              // チャイルド/ブースター/ジュニアシートのマーク（0台の種別は出さない。2026-08-09）
              // 種別ごとにはっきり色を変える（小さくても種類が一目で分かるよう塗り+白文字）
              const seatMarks = [
                { key: 'C', label: 'チャイルドシート', count: Number(detail.child_seat) || 0, color: '#e11d48' },
                { key: 'B', label: 'ブースターシート', count: Number(detail.booster_seat) || 0, color: '#2563eb' },
                { key: 'J', label: 'ジュニアシート', count: Number(detail.junior_seat) || 0, color: '#f59e0b' },
              ].filter(s => s.count > 0);

              const vehicleTag = (
                <span
                  draggable={!!vehicle && !!onVehicleSwap}
                  onDragStart={(e) => {
                    if (!vehicle || !onVehicleSwap) return;
                    e.stopPropagation();
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', vehicle.vehicle_code);
                    onVehicleDragSourceChange?.(row);
                  }}
                  onDragOver={(e) => {
                    if (isValidDropTarget) e.preventDefault();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (vehicleDragSource && vehicle && String(row.id) !== String(vehicleDragSource.id)) {
                      onVehicleSwap?.(vehicleDragSource, row);
                    }
                    onVehicleDragSourceChange?.(null);
                  }}
                  onDragEnd={() => onVehicleDragSourceChange?.(null)}
                  onClick={(e) => { e.stopPropagation(); vehicle && onVehicleClick && onVehicleClick(vehicle, row); }}
                  title={vehicle ? vehicle.vehicle_code : String(row.vehicle_id || '')}
                  style={{
                    ...vehicleOriginalStyle,
                    cursor: vehicle && onVehicleSwap ? 'grab' : vehicle ? 'pointer' : 'default',
                    fontWeight: vehicle ? 'bold' : 'normal',
                    opacity: isDragSource ? 0.4 : 1,
                    outline: isValidDropTarget ? '2px dashed #3b82f6' : 'none',
                    outlineOffset: '2px',
                    borderRadius: isValidDropTarget ? '4px' : undefined,
                    transition: 'outline 0.15s, opacity 0.15s',
                    // 末尾4桁だけ表示。3桁でも4桁分の幅を確保して列を揃える
                    // 車両未アサイン(空表示)でも幅が潰れないよう flexShrink も止める
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    minWidth: '4ch',
                    textAlign: 'center',
                    flexShrink: 0,
                  }}
                >
                  {vehicle ? shortVehicleCode(vehicle.vehicle_code) : row.vehicle_id}
                </span>
              );

              const checkButton = vehicle && (Array.isArray(vehicle.check_situations) && (() => {
                const requiredItems = vehicle.check_situations.filter(item => item.require_flg === true);
                if (requiredItems.length === 0) return null;
                const allComplete = requiredItems.every(item => item.value === true);
                const nonVcRequiredItems = requiredItems.filter(item => !item.name?.toUpperCase()?.includes('VC'));
                const isVcMode = !allComplete && nonVcRequiredItems.length > 0 && nonVcRequiredItems.every(item => item.value === true);
                const incompleteItems = requiredItems.filter(item => item.value !== true);
                const tooltipContent = incompleteItems.length > 0 ? incompleteItems.map(item => `${item.name}: 未`).join('\n') : '全て完了';

                // VC以外の未完了項目を判定（清掃・洗車の単独残り表示）
                const nonVcIncomplete = nonVcRequiredItems.filter(item => item.value !== true);
                const isCleaningOnly = !allComplete && !isVcMode && nonVcIncomplete.length === 1 && (nonVcIncomplete[0].name?.includes('清掃') || nonVcIncomplete[0].name?.toLowerCase()?.includes('cleaning'));
                const isCarWashOnly = !allComplete && !isVcMode && nonVcIncomplete.length === 1 && (nonVcIncomplete[0].name?.includes('洗車') || nonVcIncomplete[0].name?.toLowerCase()?.includes('car_wash') || nonVcIncomplete[0].name?.toLowerCase()?.includes('wash'));

                // ラベル・色の決定
                let checkLabel = '未';
                let checkBg = '#fdecea';
                let checkBorder = '#d32f2f';
                let checkColor = '#d32f2f';
                let checkHoverBg = '#ffcdd2';
                if (allComplete) {
                  checkLabel = 'OK'; checkBg = '#e8f5e9'; checkBorder = '#2e7d32'; checkColor = '#2e7d32'; checkHoverBg = '#c8e6c9';
                } else if (isCleaningOnly) {
                  checkLabel = '清掃'; checkBg = '#fff3e0'; checkBorder = '#e65100'; checkColor = '#e65100'; checkHoverBg = '#ffe0b2';
                } else if (isCarWashOnly) {
                  checkLabel = '洗車'; checkBg = '#e0f7fa'; checkBorder = '#00838f'; checkColor = '#00838f'; checkHoverBg = '#b2ebf2';
                } else if (isVcMode) {
                  checkLabel = 'VC'; checkBg = '#e3f2fd'; checkBorder = '#1976d2'; checkColor = '#1976d2'; checkHoverBg = '#bbdefb';
                }

                return (
                  <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{tooltipContent}</Box>} arrow>
                    <Button
                      variant="outlined" size="small"
                      onClick={(e) => { e.stopPropagation(); setCheckModalVehicle(vehicle); setCheckModalItems(vehicle.check_situations.map(item => ({ ...item }))); setCheckModalOpen(true); }}
                      sx={{
                        minWidth: '28px', padding: '2px 6px', fontSize: '0.65rem', fontWeight: 'bold',
                        backgroundColor: checkBg,
                        borderColor: checkBorder,
                        color: checkColor,
                        '&:hover': { backgroundColor: checkHoverBg, borderColor: checkBorder }
                      }}
                    >
                      {checkLabel}
                    </Button>
                  </Tooltip>
                );
              })());

              // 車両ステータスの色は「ステータス自身が持つ指定色」のみを使う
              // （車両色・車両タイプ色へのフォールバックはステータスの意味を壊すため行わない）
              const vStatusBg = statusInfo?.bg_color || '#f1f5f9';
              const vStatusText = statusInfo?.text_color || '#475569';
              const vehicleStatusButton = vehicle && (
                <Button
                  variant="contained" size="small"
                  onClick={(e) => { e.stopPropagation(); onStatusClick && onStatusClick(vehicle, row); }}
                  sx={{
                    fontSize: '0.65rem', padding: '2px 8px', minWidth: 'auto', boxShadow: 'none',
                    backgroundColor: vStatusBg, color: vStatusText,
                    '&:hover': { boxShadow: '0 1px 2px rgba(0,0,0,0.2)', backgroundColor: vStatusBg, filter: 'brightness(0.9)' }
                  }}
                >
                  {vehicle.status_name || '未設定'}
                </Button>
              );

              const locationStatusButton = vehicle && (
                <Button
                  variant={(() => {
                    const status = locationStatusList?.find(s => s.id === Number(vehicle.location_status));
                    return status?.bg_color ? "contained" : "outlined";
                  })()}
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onLocationStatusClick && onLocationStatusClick(vehicle, rowType, row); }}
                  sx={{
                    fontSize: '0.65rem', padding: '2px 6px', minWidth: 'auto',
                    ...(() => {
                      const status = locationStatusList?.find(s => s.id === Number(vehicle.location_status));
                      if (status?.bg_color) {
                        return {
                          backgroundColor: status.bg_color, borderColor: status.bg_color, color: status.text_color || '#fff',
                          '&:hover': { backgroundColor: `${status.bg_color}dd`, borderColor: status.bg_color, filter: 'brightness(0.95)' }
                        };
                      }
                      return {
                        backgroundColor: vehicle.location_status ? '#e3f2fd' : '#f5f5f5',
                        borderColor: vehicle.location_status ? '#1565c0' : '#bdbdbd',
                        color: vehicle.location_status ? '#1565c0' : '#757575',
                        '&:hover': { backgroundColor: vehicle.location_status ? '#bbdefb' : '#eeeeee', borderColor: vehicle.location_status ? '#1565c0' : '#9e9e9e' }
                      };
                    })(),
                    fontWeight: 500,
                    ...(vehicle.location_status && vehicle.location_status_name && vehicle.location_status_name !== '-' && {
                      animation: `${keyframes`0%, 100% { opacity: 1; } 50% { opacity: 0.4; }`} 3s ease-in-out infinite`
                    }),
                  }}
                >
                  {vehicle.location_status_name || '-'}
                </Button>
              );

              // Square Payment Section (DS1: search_by_contact.php)
              const contactId = row.contact_code || detail.contact_id;
              const paymentEntry = squarePaymentCache[contactId];
              const sumPrice = row.total_amount || detail.sum_price;
              const isManualPaid = Number(detail.payment) === 1;
              const paymentStatus = computePaymentStatus(paymentEntry, isManualPaid, squarePaymentLoading);

              const paymentStatusComp = (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    cursor: onPaymentCheck ? 'pointer' : 'default',
                    padding: '4px',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.04)'
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onPaymentCheck) onPaymentCheck(row);
                  }}
                >
                  {(paymentEntry || paymentStatus.text) ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{paymentStatus.totalPaidAmount > 0 ? paymentStatus.totalPaidAmount.toLocaleString() : '-'}</Typography>
                        {paymentStatus.text && (
                          <Typography variant="caption" sx={{ color: paymentStatus.color, fontWeight: 'bold', fontSize: '0.65rem' }}>{paymentStatus.text}</Typography>
                        )}
                      </Box>
                      {paymentStatus.hasRefunds && (
                        <Typography variant="caption" sx={{ color: '#f44336', fontWeight: 'bold', fontSize: '0.6rem' }}>返金有</Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {squarePaymentLoading ? '...' : '-'}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>({sumPrice ? Number(sumPrice).toLocaleString() : '-'})</Typography>
                </Box>
              );

              if (isSimpleView) {
                return (
                  <React.Fragment key={row.id}>
                     <TableRow
                      hover
                      onClick={() => toggleRowExpansion(row.id)}
                      onMouseEnter={() => onHoverVehicle && onHoverVehicle(row.vehicle_id)}
                      onMouseLeave={() => onLeaveVehicle && onLeaveVehicle()}
                      sx={{
                        backgroundColor: isExpanded ? '#f0f7ff' : (isDoneRow ? DONE_ROW_BG : (rowType === 'return' ? '#f5f5f5' : 'inherit')),
                        cursor: 'pointer',
                        ...rowOutline,
                      }}
                    >
                      <TableCell sx={{ borderLeft: `5px solid ${borderLeftColor}`, py: 1.5, px: '10px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, overflow: 'hidden' }}>
                            {!isExpanded && visibleColumns.time && (
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: isDoneRow ? UI.color.textSub : 'primary.main', minWidth: '45px', whiteSpace: 'nowrap' }}>
                                {isDoneRow ? '✓ ' : ''}{formatTime(time)}
                              </Typography>
                            )}
                            {visibleColumns.vehicle && vehicleTag}
                            {/* Icons moved to edges or removed from here as per request */}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {!isExpanded && (
                              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                {visibleColumns.vehicle && (
                                  <>
                                    {checkButton}
                                    {vehicleStatusButton}
                                    {locationStatusButton}
                                  </>
                                )}
                              </Box>
                            )}
                            {isExpanded ? <ExpandLessIcon color="action" /> : <ExpandMoreIcon color="action" />}
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ p: 0, borderBottom: isExpanded ? 'none' : 'inherit' }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderBottom: '1px solid #ddd' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              {/* Layout similar to the image */}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  {(visibleColumns.name || visibleColumns.license || visibleColumns.payment) && (
                                    <>
                                      <Box>
                                        {visibleColumns.name && (
                                          <>
                                            {nameSection}
                                            <Box sx={{ mt: 1 }}>
                                              {staffButtons}
                                            </Box>
                                          </>
                                        )}
                                      </Box>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', mt: 0.4 }}>
                                        {agreementIcon}
                                        {visibleColumns.license && licenseIcon}
                                        {visibleColumns.payment && paymentIcon}
                                      </Box>
                                    </>
                                  )}
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                                  {visibleColumns.vehicle && (
                                    <>
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {vehicleTag}
                                      </Box>
                                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        {checkButton}
                                        {vehicleStatusButton}
                                        {locationStatusButton}
                                      </Box>
                                    </>
                                  )}
                                </Box>
                              </Box>

                              {/* Details specific to full view */}
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1, pt: 1, borderTop: '1px dashed #ccc' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    {visibleColumns.time && (
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">時間: </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{formatTime(time)}</Typography>
                                      </Box>
                                    )}
                                    {visibleColumns.pax && (
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">人数: </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{pax}{detail.pax_children ? `(${detail.pax_children})` : ''}</Typography>
                                      </Box>
                                    )}
                                    {visibleColumns.flight && flight && (
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">{rowType === 'departure' ? 'ARR' : 'DEP'}: </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{flight}</Typography>
                                      </Box>
                                    )}
                                    {visibleColumns.status && (
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">ステータス: </Typography>
                                        <Typography variant="body2">
                                          <span style={{ backgroundColor: statusOpt?.bg_color || '#eee', color: statusOpt?.text_color || '#000', padding: '1px 4px', borderRadius: '3px', fontSize: '0.75rem', fontWeight: 'bold' }}>
                                            {statusName}
                                          </span>
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                  {visibleColumns.payment && paymentStatusComp}
                                </Box>

                                {visibleColumns.contact && (
                                  <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="caption" color="text.secondary">連絡手段: </Typography>
                                    {(() => {
                                      const tools = Array.isArray(detail.communication_tools) ? detail.communication_tools : [];
                                      const PRIORITY = ['LINE', 'WhatsApp', 'SMS', 'TEL', 'Instagram', 'その他'];
                                      const selected = tools
                                        .map(id => COMMUNICATION_TOOLS.find(t => t.id === id))
                                        .filter(Boolean)
                                        .sort((a, b) => PRIORITY.indexOf(a.label) - PRIORITY.indexOf(b.label));

                                      const topTool = selected[0];
                                      const colors = topTool ? (COMMUNICATION_MEDIA_COLORS[topTool.label] || COMMUNICATION_MEDIA_COLORS.default) : null;

                                      return (
                                        <Button
                                          variant={topTool ? "contained" : "outlined"}
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setContactModalReservation(row);
                                            setSelectedTools(tools);
                                            setContactModalOpen(true);
                                          }}
                                          sx={{
                                            fontSize: '0.65rem', padding: '2px 8px', minWidth: 'auto', boxShadow: 'none',
                                            ...(topTool ? { backgroundColor: colors.bg_color, color: colors.text_color, borderColor: colors.bg_color } : { borderColor: '#bdbdbd', color: '#424242' })
                                          }}
                                        >
                                          {topTool ? (selected.length > 1 ? `${topTool.label} +${selected.length - 1}` : topTool.label) : '未設定'}
                                        </Button>
                                      );
                                    })()}
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              }

              // --- 全行コンパクト1行表示 + クリックで詳細カード展開 ---
              // クリックで開閉（hover展開は廃止）
              const isRowOpen = !!expandedDoneRows[row.id];
              const showReturnDot = rowType === 'return' && (
                row.rowAttention?.color === TURNAROUND_COLOR || row.rowAttention?.color === TURNAROUND_SAME_TIME_COLOR
              );
              const showDepDot = rowType === 'departure' && row.isTurnaroundDeparture;

              // 受/免/支 ミニチップ（絵文字アイコンの置き換え）
              // 完了=信号機並みのはっきりした緑（塗り+白文字）。パステルの予約ステータス指定色とは
              // 彩度で明確に区別できる。未完了=白地に赤でアラート、パッケージ=青塗り「P」
              // 2026-07-26: クリック操作対応（受/免=トグル保存、支=Squareモーダル）
              const checkMini = (label: string, ok: boolean, tooltip: string, special?: 'pkg' | 'carmore', onClickChip?: (e: any) => void) => (
                <Tooltip title={tooltip} arrow key={label}>
                  <Box
                    component="span"
                    onClick={onClickChip ? (e) => { e.stopPropagation(); onClickChip(e); } : undefined}
                    sx={{
                      // サイズは車両チェックバッジ（掃/洗/M = 15px角）に統一（2026-08-10）
                      width: '15px', height: '15px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      boxSizing: 'border-box', fontSize: '0.55rem', fontWeight: 700, p: 0, borderRadius: '3px', lineHeight: 1,
                      backgroundColor: special === 'pkg' ? '#2563eb' : special === 'carmore' ? '#0d9488' : (ok ? '#16a34a' : '#fff'),
                      color: (special === 'pkg' || special === 'carmore') ? '#fff' : (ok ? '#fff' : '#dc2626'),
                      border: `1px solid ${special === 'pkg' ? '#1d4ed8' : special === 'carmore' ? '#0f766e' : (ok ? '#15803d' : '#fca5a5')}`,
                      whiteSpace: 'nowrap', flexShrink: 0,
                      ...(onClickChip ? { cursor: 'pointer', '&:hover': { boxShadow: '0 0 0 2px rgba(37,99,235,0.35)' } } : {}),
                    }}
                  >{label}</Box>
                </Tooltip>
              );

              // 受/免のトグル保存（専用関数 + 同期キャッシュ即時反映）
              const toggleFlagAndSync = async (kind: 'license' | 'agreement') => {
                const current = kind === 'license' ? isLicense : isAgreement;
                const next = current ? 0 : 1;
                try {
                  const saved = kind === 'license'
                    ? await toggleReservationLicense(row, next)
                    : await toggleReservationAgreement(row, next);
                  if (saved) reservationSyncService.applyLocalUpdate(saved);
                } catch (err) {
                  console.error(`[ReservationTable] ${kind} toggle failed:`, err);
                }
              };

              // ターンアラウンド/次回予約インジケータ（i/!丸）
              const attIndicator = (() => {
                const att = rowType === 'return' ? (row.rowAttention || (vehicle && upcomingStatuses?.[vehicle.id])) : null;
                const isTurnaround = rowType === 'departure' && row.isTurnaroundDeparture;
                if (att && !(row.rowAttention === null && row.hasOwnProperty('rowAttention'))) {
                  return (
                    <Tooltip title={att.label} arrow>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 15, height: 15, minWidth: 15, borderRadius: '50%', backgroundColor: att.color, color: '#fff', fontSize: '10px', fontWeight: 'bold', fontFamily: 'serif', cursor: 'help', flexShrink: 0 }}>i</Box>
                    </Tooltip>
                  );
                }
                if (isTurnaround) {
                  // 返却時刻と貸出時刻が同時刻ならショッキングピンク（2026-08-25）
                  const sameTime = !!row.isSameTimeTurnaround;
                  return (
                    <Tooltip title={sameTime ? '返却と同時刻に貸出（ターンアラウンド）' : '当日返却あり（ターンアラウンド）'} arrow>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 15, height: 15, minWidth: 15, borderRadius: '50%', backgroundColor: sameTime ? TURNAROUND_SAME_TIME_COLOR : TURNAROUND_COLOR, color: '#fff', fontSize: '11px', fontWeight: 'bold', cursor: 'help', flexShrink: 0 }}>!</Box>
                    </Tooltip>
                  );
                }
                return <Box sx={{ width: 15, height: 15, minWidth: 15, borderRadius: '50%', backgroundColor: UI.color.bgHover, flexShrink: 0 }} />;
              })();

              // 行カードの見た目。統合表示(mergedView)ではインデントは使わず全行フル幅
              // （時間・車両コードの列位置を揃える）。返却は行頭の↩アイコンで区別し、
              // ターンアラウンドペア連結（__pairWithNext/__pairWithPrev: 赤破線+境界チップで上下結合）
              const rowCardSx = mergedView ? {
                position: 'relative',
                border: `1px solid ${UI.color.border}`,
                borderLeft: `5px solid ${borderLeftColor}`,
                borderRadius: 1,
                // 完了行は淡い緑で明示（返却の紫ベースより優先。2026-07-26 視認性改善）。
                // 開いている行は青が最優先。
                backgroundColor: isRowOpen
                  ? UI.color.accentBg
                  : (isDoneRow ? DONE_ROW_BG : (rowType === 'return' ? '#f5eefa' : '#fff')),
                // 対応中は黄色点滅（開いている行の青枠はそのまま重ねる）
                ...(isInProgressRow ? { animation: `${IN_PROGRESS_PULSE} 1.6s ease-in-out infinite` } : {}),
                boxShadow: isRowOpen ? `inset 0 0 0 2px ${UI.color.accent}` : 'none',
                mx: 0.5,
                mb: row.__pairWithNext ? 0 : 1.2,
                ...(row.__pairWithNext ? { borderBottomLeftRadius: 0, borderBottomRightRadius: 0, borderBottom: '1px dashed #ef4444' } : {}),
                ...(row.__pairWithPrev ? { borderTopLeftRadius: 0, borderTopRightRadius: 0, borderTop: 'none' } : {}),
                // ホテル配車は濃い青緑・クルーズ船は濃い青の点線の枠（2026-10-25・11-02。outline なので行の大きさは変わらない）
                ...rowOutline,
              } : {
                position: 'relative',
                borderLeft: `5px solid ${borderLeftColor}`,
                borderBottom: `1px solid ${UI.color.border}`,
                backgroundColor: isDoneRow ? DONE_ROW_BG : (isRowOpen ? UI.color.accentBg : '#fff'),
                // 対応中は黄色点滅（開いている行の青枠はそのまま重ねる）
                ...(isInProgressRow ? { animation: `${IN_PROGRESS_PULSE} 1.6s ease-in-out infinite` } : {}),
                // hover/ピン留め中の行はアクセント枠で明示
                boxShadow: isRowOpen ? `inset 0 0 0 2px ${UI.color.accent}` : 'none',
                // ホテル配車は濃い青緑・クルーズ船は濃い青の点線の枠（2026-10-25・11-02）
                ...rowOutline,
              };

              return (
                <React.Fragment key={`${rowType}-${row.id}`}>
                  {/* 統合表示: 30分グリッドの見出し（スロットごとに交互の帯色） */}
                  {mergedView && row.__slotLabel && (
                    <TableRow>
                      <TableCell sx={{ border: 'none', pt: 0.6, pb: 0.3, px: 1, backgroundColor: row.__slotEven ? '#eef2f7' : 'transparent' }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: UI.color.textSub }}>
                          {row.__slotLabel}〜
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                <TableRow
                  onMouseEnter={() => onHoverVehicle && onHoverVehicle(row.vehicle_id)}
                  onMouseLeave={() => onLeaveVehicle && onLeaveVehicle()}
                >
                  <TableCell sx={mergedView ? { p: 0, border: 'none', backgroundColor: row.__slotEven ? '#eef2f7' : 'transparent' } : { p: 0, border: 'none' }}>
                  <Box sx={rowCardSx}>
                    {(showReturnDot || showDepDot) && (
                      <Box
                        className="turnaround-dot"
                        data-vehicle-id={row.vehicle_id}
                        data-type={rowType}
                        sx={{ position: 'absolute', ...(rowType === 'return' ? { left: '-14px' } : { right: '-9px' }), top: 12, width: 18, height: 18, opacity: 0, pointerEvents: 'none' }}
                      />
                    )}
                    {/* 統合表示: ペア境界に「同一車両 連続」チップ（返却→すぐ貸出を直感的に示す） */}
                    {mergedView && row.__pairWithPrev && (
                      <Box sx={{
                        position: 'absolute', top: -9, left: '50%', transform: 'translateX(-50%)', zIndex: 2,
                        fontSize: '0.6rem', fontWeight: 700, whiteSpace: 'nowrap', pointerEvents: 'none',
                        backgroundColor: '#fef2f2', color: '#b91c1c', border: '1px solid #fca5a5',
                        borderRadius: '8px', px: 0.8, lineHeight: 1.5,
                      }}>
                        ⟳ 同一車両 連続
                      </Box>
                    )}
                    {/* コンパクト行（2段構成 2026-08-10）:
                        1段目=時間/車両/ステータス/名前/人数/予約状態、
                        2段目=各列のサブ情報（車両コード下=掃洗M、名前下=受免支、人数下=シートC/B/J）。
                        列ごとに縦flexにすることで、名前列が縮んでも上下の対応がずれない */}
                    {/* 2026-08-10: アコーディオン展開（詳細カード）は廃止。全情報を2段行に集約 */}
                    <Box sx={{ position: 'relative', px: 1, py: 0.4, minWidth: 0 }}>
                      {/* 2026-07-27: flexWrap を廃止（nowrap）。ウィンドウ幅を狭めた時に行内で
                          折り返すと列の並びが崩れて視認性が落ちるため、全要素は固定幅のまま
                          「名前列だけ」縮小（ellipsis）して1行を維持する */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.8, flexWrap: 'nowrap', minWidth: 0, overflow: 'hidden' }}>
                      {/* 統合表示: 行頭の固定幅スロット（返却=↩ / 貸出=空）。両タイプ同幅で列を揃える */}
                      {mergedView && (
                        <Box component="span" sx={{ ...cellTopSx, width: 18, minWidth: 18, justifyContent: 'center' }}>
                          {rowType === 'return' && (
                            <Tooltip title="返却" arrow>
                              <KeyboardReturnIcon sx={{ fontSize: '0.95rem', color: '#7b1fa2' }} />
                            </Tooltip>
                          )}
                        </Box>
                      )}
                      {/* ✓は固定幅スロットで列ずれを防ぐ（完了=緑丸バッジで明示。2026-07-26） */}
                      <Box component="span" sx={{ ...cellTopSx, width: 16, minWidth: 16, justifyContent: 'center' }}>
                        {isDoneRow ? (
                          <Box component="span" sx={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 15, height: 15, borderRadius: '50%', bgcolor: '#16a34a', color: '#fff',
                            fontSize: '0.62rem', fontWeight: 900, lineHeight: 1,
                          }}>✓</Box>
                        ) : null}
                      </Box>
                      <Box sx={cellTopSx}>{attIndicator}</Box>
                      {/* 1. 時間列（2段: 上=時刻 / 下=プラン接頭🚶‍♀️+メモ⚠️。2026-08-10 移動）
                             下段は無い側を visibility:hidden にして幅を確定させ、列ずれを防ぐ */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: ROW_LINE_GAP, minWidth: 42, flexShrink: 0 }}>
                        <Box sx={cellTopSx}>
                          <Tooltip title={fmEst ? `フライト見込 ${fmEst}${fmOverdue ? '（見込み超過・未出発）' : ''}` : ''} arrow>
                            <Typography component="span" sx={{ fontSize: '0.8rem', fontWeight: 700, color: compactTimeColor, flexShrink: 0 }}>
                              {formatTime(time)}
                            </Typography>
                          </Tooltip>
                        </Box>
                        <Box sx={{ ...cellBotSx, gap: '1px' }}>
                          {/* 車両ポジションの該当カードへジャンプ（2026-08-10 追加）。
                              車両未アサインの行では出さない */}
                          {onLocateVehicle && row.vehicle_id != null && (
                            <Tooltip title="車両ポジションの該当車両へ移動" arrow>
                              <Box
                                component="span"
                                onClick={(e) => { e.stopPropagation(); onLocateVehicle(row.vehicle_id); }}
                                sx={{
                                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                  width: 14, height: 14, borderRadius: '3px', cursor: 'pointer',
                                  fontSize: '0.6rem', lineHeight: 1, color: UI.color.accent,
                                  border: `1px solid ${UI.color.border}`, backgroundColor: '#fff',
                                  '&:hover': { backgroundColor: UI.color.accentBg, borderColor: UI.color.accent },
                                }}
                              >⬇</Box>
                            </Tooltip>
                          )}
                          <Tooltip title={planPrefixTitle || ''} arrow>
                            <Box component="span" data-testid="plan-prefix" sx={{
                              fontSize: '0.8rem', lineHeight: 1, width: '1.1em', textAlign: 'center',
                              visibility: planPrefix ? 'visible' : 'hidden',
                              ...hotelDotSx,
                            }}>{planPrefix || '🚶'}</Box>
                          </Tooltip>
                          <Tooltip title={row.short_memo || ''} arrow>
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                if (row.short_memo) setShortMemoModal({ open: true, row: row, value: row.short_memo || '' });
                              }}
                              style={{
                                fontSize: '0.8rem', lineHeight: 1, width: '1.1em', textAlign: 'center',
                                cursor: row.short_memo ? 'pointer' : 'default',
                                visibility: row.short_memo ? 'visible' : 'hidden',
                              }}
                            >⚠️</span>
                          </Tooltip>
                        </Box>
                      </Box>
                      {/* 2. 車両コード（末尾4桁）+ 2段目: 車両チェックバッジ（掃/洗/M） */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: ROW_LINE_GAP, flexShrink: 0 }}>
                        <Box sx={cellTopSx}>{vehicleTag}</Box>
                        <Box sx={cellBotSx}>
                          {vehicle && Array.isArray(vehicle.check_situations) && (() => {
                            const reqChecks = vehicle.check_situations.filter((c: any) => c.require_flg === true);
                            if (reqChecks.length === 0) return null;
                            return (
                              <Box
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCheckModalVehicle(vehicle);
                                  setCheckModalItems(vehicle.check_situations.map((item: any) => ({ ...item })));
                                  setCheckModalOpen(true);
                                }}
                                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', borderRadius: '4px', '&:hover': { backgroundColor: UI.color.bgHover } }}
                              >
                                <VehicleCheckBadges checkSituations={vehicle.check_situations} />
                              </Box>
                            );
                          })()}
                        </Box>
                      </Box>
                      {/* 3. 車両ステータス（畳んだ状態でも表示・固定幅+中央揃えで列を揃える 2026-07-27）
                             車両未アサインの行でも同じ幅の空スロットを置く。条件で消すと
                             以降の列が左に詰まって列ずれになるため（2026-08-09 修正） */}
                      {/* 3. ステータス列（2段: 上=車両ステータス(出発中) / 下=予約ステータス(利用中) 2026-08-10）
                             ラベルは3文字幅に揃うので、左右の余白を詰めた固定幅にする */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: ROW_LINE_GAP, flexShrink: 0 }}>
                        <Box sx={cellTopSx}>
                          {vehicle && vehicle.status_name ? (
                            <Tooltip title={vehicle.status_name} arrow>
                              <span
                                onClick={(e) => { e.stopPropagation(); onStatusClick && onStatusClick(vehicle, row); }}
                                style={{ ...STATUS_CHIP_BASE, backgroundColor: vStatusBg, color: vStatusText }}
                              ><SpacedLabel label={truncStatusName(vehicle.status_name)} /></span>
                            </Tooltip>
                          ) : (
                            <span style={{ display: 'inline-block', width: STATUS_CHIP_WIDTH, flexShrink: 0 }} />
                          )}
                        </Box>
                        <Box sx={cellBotSx}>
                          <Tooltip title={statusName} arrow>
                            <span
                              onClick={(e) => { e.stopPropagation(); setResStatusModal({ open: true, row: row }); }}
                              style={{ ...STATUS_CHIP_BASE, backgroundColor: statusOpt?.bg_color || '#eee', color: statusOpt?.text_color || '#000' }}
                            ><SpacedLabel label={truncStatusName(statusName)} /></span>
                          </Tooltip>
                        </Box>
                      </Box>
                      {/* 4. 予約者名（6文字幅で統一・truncate・Tooltipで全文・クリックで編集パネル）
                          幅が足りない時はこの列だけ縮む（他列は固定幅を維持して列ずれを防ぐ） */}
                      {/* 4. 名前列（2段: 上=名前 / 下=受免支。受免支のサイズは掃洗Mバッジに合わせる） */}
                      {/* 2026-08-10: 名前は1文字分短く truncate（6.6em → 5.6em） */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: ROW_LINE_GAP, width: '5.6em', minWidth: '3em', flexShrink: 1 }}>
                        <Box sx={{ ...cellTopSx, width: '100%' }}>
                          <Tooltip title={name} arrow>
                            <span
                              onClick={(e) => { e.stopPropagation(); handleOpenReservation(row); }}
                              style={{ ...tagStyle, fontSize: '0.8rem', fontWeight: 'bold', cursor: 'pointer', display: 'block', boxSizing: 'border-box', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >{name}</span>
                          </Tooltip>
                        </Box>
                        {/* 7-9. 受 / 免 / 支（2026-08-10 名前の下段へ移動） */}
                        <Box sx={{ ...cellBotSx, gap: 0.4 }}>
                          {checkMini('受', isAgreement, `事前受付: ${isAgreement ? '完了' : '未完了'}（クリックで切替）`, undefined,
                            () => toggleFlagAndSync('agreement'))}
                          {checkMini('免', isLicense, `免許証: ${isLicense ? '完了' : '未完了'}（クリックで切替）`, undefined,
                            () => toggleFlagAndSync('license'))}
                          {(!isPayment && isCarmore)
                            ? checkMini('C', true, 'CarMore経由（支払い済み・確認不要）', 'carmore',
                              onPaymentCheck ? () => onPaymentCheck(row) : undefined)
                            : (!isPayment && isPkg)
                              ? checkMini('P', true, 'パッケージ（クリックで支払い確認）', 'pkg',
                                onPaymentCheck ? () => onPaymentCheck(row) : undefined)
                              : checkMini('支', isPayment, `支払い: ${isPayment ? '完了' : '未完了'}（クリックで支払い確認）`, undefined,
                                onPaymentCheck ? () => onPaymentCheck(row) : undefined)}
                        </Box>
                      </Box>
                      {/* 4a. 人数列（2段: 上=総人数(うち子供n名)「7(3)」 / 下=シートマーク C1/B1/J1） */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: ROW_LINE_GAP, width: '3.6em', minWidth: '3.6em', flexShrink: 0 }}>
                        <Box sx={{ ...cellTopSx, justifyContent: 'center', width: '100%' }}>
                          <Tooltip title={`人数: ${pax || 0}名${detail.pax_children ? `（うち子供 ${detail.pax_children}名）` : ''}`} arrow>
                            <Typography component="span" sx={{
                              fontSize: '0.65rem', fontWeight: 700, color: UI.color.textSub,
                              textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', maxWidth: '100%',
                            }}>
                              {pax || ''}{detail.pax_children ? `(${detail.pax_children})` : ''}
                            </Typography>
                          </Tooltip>
                        </Box>
                        {/* シートマークは人数と同じ文字サイズにする（2026-08-10） */}
                        <Box sx={{ ...cellBotSx, justifyContent: 'center', gap: '2px', width: '100%', overflow: 'hidden' }}>
                          {seatMarks.map(m => (
                            <Tooltip key={m.key} title={`${m.label} ${m.count}台`} arrow>
                              <Box component="span" sx={{
                                display: 'inline-flex', alignItems: 'center',
                                fontSize: '0.65rem', fontWeight: 800, lineHeight: 1,
                                px: '2px', borderRadius: '2px', whiteSpace: 'nowrap',
                                bgcolor: m.color, color: '#fff',
                              }}>{m.key}{m.count}</Box>
                            </Tooltip>
                          ))}
                        </Box>
                      </Box>
                      {/* プラン接頭(🚶‍♀️) とメモ(⚠️) は 2026-08-10 に時間列の下段へ移動 */}
                      {/* 5. LINE列（2段: 上=吹き出し（LINE 緑・自社チャット 青） / 下=連絡手段 2026-08-10）。
                             LINE 無効店舗(宮古)では吹き出しなしで連絡手段のみ。
                             2026-08-10: 連絡手段チップ(LINE+1 / 未設定 等)で幅が変わり以降の列が
                             ずれていたため、列幅を固定して溢れは内側で隠す */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: ROW_LINE_GAP, flexShrink: 0, width: '3.2rem', minWidth: '3.2rem', overflow: 'hidden' }}>
                        <Box sx={{ ...cellTopSx, justifyContent: 'center', width: '100%' }}>
                        {lineEnabled && (
                        <Tooltip title={lineUserData ? (lineUserData.unread_count > 0 ? `LINE未読 ${lineUserData.unread_count}件` : 'LINEチャットを開く') : 'LINE未連携'} arrow>
                          <Box
                            component="span"
                            onClick={(e) => { e.stopPropagation(); if (lineUserData && onOpenLineChat) onOpenLineChat(lineUserData, row.id); }}
                            sx={{ position: 'relative', display: 'inline-flex', cursor: lineUserData ? 'pointer' : 'default', flexShrink: 0, lineHeight: 1, opacity: lineUserData ? 1 : 0.3, filter: lineUserData ? 'none' : 'grayscale(1)' }}
                          >
                            <ChatBubbleIcon data-testid="line-chat-bubble" sx={{ fontSize: '0.9rem', display: 'block', color: lineUserData ? LINE_CHAT_COLOR : CHAT_ICON_DISABLED_COLOR }} />
                            {lineUserData?.unread_count > 0 && (
                              <Box sx={{ position: 'absolute', top: -2, right: -3, width: 7, height: 7, bgcolor: '#ff4d4d', borderRadius: '50%', border: '1px solid #fff' }} />
                            )}
                          </Box>
                        </Tooltip>
                        )}
                        {renderResvChatIcon(row)}
                        </Box>
                        {/* 連絡手段（旧アコーディオン内から移動） */}
                        <Box sx={{ ...cellBotSx, justifyContent: 'center', width: '100%' }}>
                        {(() => {
                          const tools = Array.isArray(detail.communication_tools) ? detail.communication_tools : [];
                          const PRIORITY = ['LINE', 'WhatsApp', 'SMS', 'TEL', 'Instagram', 'その他'];
                          const selected = tools
                            .map(id => COMMUNICATION_TOOLS.find(t => t.id === id))
                            .filter(Boolean)
                            .sort((a, b) => PRIORITY.indexOf(a.label) - PRIORITY.indexOf(b.label));
                          const topTool = selected[0];
                          const colors = topTool ? (COMMUNICATION_MEDIA_COLORS[topTool.label] || COMMUNICATION_MEDIA_COLORS.default) : null;
                          return (
                            <Tooltip title={`連絡手段: ${selected.length ? selected.map(t => t.label).join(', ') : '未設定'}（クリックで変更）`} arrow>
                              <Box
                                component="span"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setContactModalReservation(row);
                                  setSelectedTools(tools);
                                  setContactModalOpen(true);
                                }}
                                sx={{
                                  // 幅は他のラベルと共通（LIST_LABEL_WIDTH）。連絡手段によって
                                  // 幅が変わると以降の列がずれるため固定する（2026-08-25）
                                  fontSize: '0.55rem', fontWeight: 700, px: '3px', py: '1px', borderRadius: '3px',
                                  lineHeight: 1.4, whiteSpace: 'nowrap', cursor: 'pointer', flexShrink: 0,
                                  display: 'inline-block', boxSizing: 'border-box',
                                  width: LIST_LABEL_WIDTH, textAlign: 'center', overflow: 'hidden',
                                  ...(topTool
                                    ? { backgroundColor: colors.bg_color, color: colors.text_color }
                                    : { border: '1px solid #bdbdbd', color: '#757575' }),
                                }}
                              >
                                {topTool
                                  ? (selected.length > 1
                                    ? `${shortToolLabel(topTool.label)}+${selected.length - 1}`
                                    : shortToolLabel(topTool.label))
                                  : '未設定'}
                              </Box>
                            </Tooltip>
                          );
                        })()}
                        </Box>
                      </Box>
                      {/* 6. 予約ステータスは 2026-08-10 に車両ステータスの下段へ移動 */}
                      {/* 7. 連絡・運転者・対応者 / ARR便 の2段列（2026-08-10 再配置）
                             上段: 連絡（直来/空港サブゾーン）+ 運転者 + 対応者
                             下段: ARR/DEP便（上段3つの下に置く） */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: ROW_LINE_GAP, minWidth: 0 }}>
                        <Box sx={{ ...cellTopSx, gap: 0.4, flexWrap: 'nowrap' }}>
                          {contactButtonEl}
                          {driverHandlerButtons}
                        </Box>
                        <Box sx={{ ...cellBotSx, gap: 0.4 }}>
                          {flight && (
                            <Tooltip title={`${rowType === 'departure' ? 'ARR便' : 'DEP便'}: ${flight}${fmEst ? ` / フライト見込 ${fmEst}` : ''}${fmStatusText ? ` / ${fmStatusText}` : ''}${fmRow?.matchedVia === 'codeshare' ? ` / 実運航 ${fmRow.arrival.carrier}${fmRow.arrival.flightNo}` : ''}${fmRow?.arrival?.remark ? ` / ${fmRow.arrival.remark}` : ''}`} arrow>
                              <Typography component="span" sx={{
                                fontSize: '0.65rem', fontWeight: 700, color: fmOverdue ? '#dc2626' : UI.color.textSub,
                                whiteSpace: 'nowrap', flexShrink: 0,
                              }}>
                                {flight}{fmEst ? `→${fmEst}` : ''}
                              </Typography>
                            </Tooltip>
                          )}
                          {/* FISステータス（降機中 / 手荷物受取中 / 欠航 等）。2026-08-10のアコーディオン廃止時に
                              消えてしまっていたので下段のフライト情報の右に復活（2026-08-24） */}
                          {flight && fmStatusText && (
                            <Box component="span" sx={{
                              px: '3px', borderRadius: '3px', fontSize: '0.6rem', fontWeight: 700,
                              lineHeight: 1.4, whiteSpace: 'nowrap', flexShrink: 0,
                              backgroundColor: fmStatusColor.bg, color: fmStatusColor.text,
                            }}>
                              {fmStatusText}
                            </Box>
                          )}
                        </Box>
                      </Box>
                      {/* 受免支は 2026-08-10 に名前の下段へ、掃洗Mは車両コードの下段へ移動 */}
                      {/* プラン接頭(🚶‍♀️/🚃) とメモ(⚠️) は 2026-08-09 に名前の右へ移動 */}
                      </Box>
                    </Box>

                  </Box>
                  </TableCell>
                </TableRow>
                </React.Fragment>
              );
            })}
            {filteredData.length === 0 && (
              <TableRow>
                <TableCell colSpan={Object.values(actualVisibleColumns).filter(Boolean).length} align="center">
                  No reservations found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <ReservationEditorPanel
        open={Boolean(selectedReservation)}
        editing={editingReservation}
        onCancel={() => setSelectedReservation(null)}
        onChange={(newFields) => setEditingReservation(prev => ({ ...prev, ...newFields }))}
        onSave={async (data: any) => {
          if (onReservationSave && editingReservation) {
            try {
              // 第1引数 = パネルが三方向マージ済みのペイロード（2026-09-07）
              await onReservationSave(data || editingReservation);
              setSelectedReservation(null);
            } catch (e) {
              console.error('Failed to save reservation:', e);
            }
          }
        }}
        disabled={false}
        registerNameInputRef={() => {}}
        reservationStatusList={statusOptions}
        vehicles={vehicles}
        vehicleTypes={vehicleTypes}
        onAssignClick={onAssignClick}
      />

      {/* 連絡手段選択モーダル */}
      <Dialog
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          連絡手段を選択
          <Typography variant="body2" color="text.secondary">
            {contactModalReservation?.customer_name}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pt: 1 }}>
            {COMMUNICATION_TOOLS.map((tool) => {
              const colors = COMMUNICATION_MEDIA_COLORS[tool.label] || COMMUNICATION_MEDIA_COLORS.default;
              const isSelected = selectedTools.includes(tool.id);
              return (
                <Chip
                  key={tool.id}
                  label={tool.label}
                  onClick={() => {
                    setSelectedTools(prev => {
                      if (prev.includes(tool.id)) {
                        return prev.filter(id => id !== tool.id);
                      } else {
                        return [...prev, tool.id].sort((a, b) => a - b);
                      }
                    });
                  }}
                  sx={{
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    backgroundColor: isSelected ? colors.bg_color : 'transparent',
                    color: isSelected ? colors.text_color : 'inherit',
                    borderColor: isSelected ? colors.bg_color : '#bdbdbd',
                    '&:hover': {
                      opacity: 0.8,
                      backgroundColor: isSelected ? colors.bg_color : '#f5f5f5',
                    },
                    border: isSelected ? 'none' : '1px solid #bdbdbd',
                  }}
                />
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactModalOpen(false)} color="inherit">
            キャンセル
          </Button>
          <Button
            onClick={async () => {
              if (onCommunicationToolsUpdate && contactModalReservation) {
                await onCommunicationToolsUpdate(contactModalReservation, selectedTools, type);
              }
              setContactModalOpen(false);
            }}
            variant="contained"
            color="primary"
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* 車両チェック項目モーダル */}
      <Dialog
        open={checkModalOpen}
        onClose={() => setCheckModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          車両チェック
          <Typography variant="body2" color="text.secondary">
            {checkModalVehicle?.vehicle_code}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}>
            {checkModalItems.map((item) => (
              <Button
                key={item.id}
                variant={item.value ? 'contained' : 'outlined'}
                onClick={() => {
                  setCheckModalItems(prev =>
                    prev.map(i => i.id === item.id ? { ...i, value: !i.value } : i)
                  );
                }}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  padding: '8px 16px',
                  backgroundColor: item.value ? (item.bg_color || '#e8f5e9') : 'transparent',
                  borderColor: item.value ? (item.bg_color || '#4caf50') : '#bdbdbd',
                  color: item.value ? (item.text_color || '#2e7d32') : '#757575',
                  fontWeight: item.value ? 'bold' : 'normal',
                  '&:hover': {
                    backgroundColor: item.value
                      ? (item.bg_color ? `${item.bg_color}dd` : '#c8e6c9')
                      : '#f5f5f5',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Checkbox
                    checked={!!item.value}
                    size="small"
                    sx={{
                      color: item.value ? 'inherit' : '#bdbdbd',
                      '&.Mui-checked': {
                        color: 'inherit',
                      },
                      padding: 0,
                      pointerEvents: 'none'
                    }}
                  />
                  <span style={{ flex: 1, textAlign: 'left' }}>{item.name}</span>
                  {item.require_flg && (
                    <Chip
                      label="必須"
                      size="small"
                      sx={{
                        height: '18px',
                        fontSize: '0.65rem',
                        backgroundColor: '#fff3e0',
                        color: '#e65100',
                      }}
                    />
                  )}
                </Box>
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCheckModalOpen(false)} color="inherit">
            キャンセル
          </Button>
          <Button
            onClick={async () => {
              if (checkModalVehicle && onCheckUpdate) {
                await onCheckUpdate(checkModalVehicle, checkModalItems);
              }
              setCheckModalOpen(false);
            }}
            variant="contained"
            color="primary"
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* Short Memo Edit Modal */}
      <Dialog
        open={shortMemoModal.open}
        onClose={() => setShortMemoModal({ ...shortMemoModal, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>直前メモ編集</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="メモ内容"
            fullWidth
            multiline
            minRows={3}
            value={shortMemoModal.value}
            onChange={(e) => setShortMemoModal({ ...shortMemoModal, value: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShortMemoModal({ ...shortMemoModal, open: false })}>キャンセル</Button>
          <Button
            onClick={() => {
              if (onQuickUpdate && shortMemoModal.row) {
                onQuickUpdate(shortMemoModal.row, { short_memo: shortMemoModal.value });
              }
              setShortMemoModal({ ...shortMemoModal, open: false });
            }}
            variant="contained"
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* Request Time Edit Modal */}
      <Dialog
        open={requestTimeModal.open}
        onClose={() => setRequestTimeModal({ ...requestTimeModal, open: false })}
        maxWidth="xs"
      >
        <DialogTitle>希望時間編集</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={requestTimeModal.field === 'request_coming_time' ? '来店希望時間' : '返却希望時間'}
            type="time"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 300 }} // 5 min step
            value={requestTimeModal.value}
            onChange={(e) => setRequestTimeModal({ ...requestTimeModal, value: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestTimeModal({ ...requestTimeModal, open: false })}>キャンセル</Button>
          <Button
            onClick={() => {
              if (onQuickUpdate && requestTimeModal.row) {
                onQuickUpdate(requestTimeModal.row, { [requestTimeModal.field]: requestTimeModal.value });
              }
              setRequestTimeModal({ ...requestTimeModal, open: false });
            }}
            variant="contained"
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* 予約ステータス変更ダイアログ */}
      <Dialog open={resStatusModal.open} onClose={() => setResStatusModal({ open: false, row: null })}>
        <DialogTitle>予約ステータス変更</DialogTitle>
        <DialogContent>
          <Grid container spacing={1} sx={{ mt: 1 }}>
            {statusOptions.map((status) => (
              <Grid item xs={6} key={status.id} {...{} as any}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleResStatusUpdate(status.id)}
                  sx={{
                    py: 1.5,
                    backgroundColor: status.bg_color || '#eee',
                    color: status.text_color || '#000',
                    '&:hover': {
                      backgroundColor: status.bg_color || '#eee',
                      filter: 'brightness(0.9)',
                    }
                  }}
                >
                  {status.label}
                </Button>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResStatusModal({ open: false, row: null })}>キャンセル</Button>
        </DialogActions>
      </Dialog>

    </Paper >
  );
}

